import {
  AUTOCOMPLETE_COUNTRY_NAMES_EXCLUDED,
  LIVE_MAP_DEBUG_TRACKING_MAX_SPEED_KMH,
  MapConst,
} from "../constants";
import { TimeZoneEnum } from "../enums";
import { ILocationInfoBase, ITrackingInfoDto } from "../models";
import { TLatLngExpression } from "../models/map.models";
import {
  calculateDateDifference,
  dateISOApplyTimezone,
  dateToComparableNumber,
} from "./date.helpers";
import { getRandomInt } from "./formula.helpers";

export const normalizeAndSortLocations = (
  locations: Partial<ILocationInfoBase>[] | undefined,
): Required<ILocationInfoBase>[] => {
  if (!locations?.length) {
    return [];
  }

  return locations
    .filter(dto => dto.arrivalTime && dto.latitude && dto.longitude)
    .sort(
      (a, b) =>
        dateToComparableNumber(a.arrivalTime) -
        dateToComparableNumber(b.arrivalTime),
    ) as Required<ILocationInfoBase>[];
};

export const normalizeTrackingPoints = (
  trackingPoints: Partial<ITrackingInfoDto>[],
): Required<ITrackingInfoDto>[] => {
  return (
    (trackingPoints.filter(
      x => x.latitude && x.longitude && x.insDate,
    ) as Required<ITrackingInfoDto>[]) || []
  );
};

export const trackingDtosToLocations = (
  trackingPoints: Partial<ITrackingInfoDto>[],
): Required<ILocationInfoBase>[] => {
  const normalizedTrackingPoints = normalizeTrackingPoints(trackingPoints);
  const locations = normalizedTrackingPoints.map(x => ({
    latitude: x.latitude as number,
    longitude: x.longitude as number,
    arrivalTime: x.insDate,
  }));

  return normalizeAndSortLocations(locations);
};

export const generateDebugTrackingInfos = (
  stationsLocations: ILocationInfoBase[],
  generateNumber: number,
  rideId: number,
): Required<ITrackingInfoDto>[] => {
  const trackingPoints: Required<ITrackingInfoDto>[] = [];
  let idCounter = 1;
  const stationsLocationsNormalized =
    normalizeAndSortLocations(stationsLocations);

  for (let i = 0; i < stationsLocationsNormalized.length - 1; i++) {
    const start = stationsLocationsNormalized[i];
    const end = stationsLocationsNormalized[i + 1];
    const stationsOffset = calculateDateDifference(
      end.arrivalTime,
      start.arrivalTime,
      "milliseconds",
    );

    for (let j = 0; j < generateNumber; j++) {
      const fraction = (j + 1) / (generateNumber + 1);

      const interpolatedLat =
        start.latitude + fraction * (end.latitude - start.latitude);
      const interpolatedLng =
        start.longitude + fraction * (end.longitude - start.longitude);

      const coordinateRandomShift = 0.5;
      const deviationUnit = 0.00115;
      const timeDeviation = 30000;
      const deviationLat =
        interpolatedLat +
        (Math.random() - coordinateRandomShift) * deviationUnit;
      const deviationLng =
        interpolatedLng +
        (Math.random() - coordinateRandomShift) * deviationUnit;
      const deviationTime = getRandomInt(-timeDeviation, timeDeviation);
      const newTime = new Date(
        dateISOApplyTimezone(
          start.arrivalTime,
          TimeZoneEnum.AsiaJerusalem,
        ).valueOf() +
          (j * stationsOffset) / generateNumber +
          deviationTime,
      );

      trackingPoints.push({
        id: idCounter++,
        rideId,
        insDate: newTime.toISOString(),
        latitude: deviationLat,
        longitude: deviationLng,
        speed: getRandomInt(0, LIVE_MAP_DEBUG_TRACKING_MAX_SPEED_KMH),
        inDetour: false,
      });
    }
  }

  return trackingPoints;
};

interface ITrackingPolylinePoint {
  startPoint: Required<ILocationInfoBase>;
  endPoint: Required<ILocationInfoBase>;
  lineColor: string;
  dashArray?: string | number[];
  positions: TLatLngExpression[];
}

/** Expects locations already normalized via {@link normalizeAndSortLocations}. */
export const createTrackingPolylines = (
  trackingLocations: Required<ILocationInfoBase>[],
  routeColor: string,
): ITrackingPolylinePoint[] => {
  const lines: ITrackingPolylinePoint[] = [];

  for (let i = 0; i < trackingLocations.length - 1; i++) {
    const startPoint = trackingLocations[i];
    const endPoint = trackingLocations[i + 1];

    const positions: TLatLngExpression[] = [
      [startPoint.latitude, startPoint.longitude],
      [endPoint.latitude, endPoint.longitude],
    ];

    const timeDiff = calculateDateDifference(
      endPoint.arrivalTime,
      startPoint.arrivalTime,
      "minute",
    );
    const lineColor =
      timeDiff < MapConst.MINUTES_LIMIT_SOLID_LINE
        ? routeColor
        : MapConst.DASHED_LINE_COLOR;
    const dashArray =
      timeDiff < MapConst.MINUTES_LIMIT_SOLID_LINE
        ? undefined
        : MapConst.DASHED_LINE_OPTIONS;

    const resItem: ITrackingPolylinePoint = {
      positions,
      lineColor,
      dashArray,
      startPoint,
      endPoint,
    };
    lines.push(resItem);
  }

  return lines;
};

export const cleanSecondaryText = (
  secondaryText: string | null | undefined,
): string => {
  if (!secondaryText) {
    return "";
  }

  const parts = secondaryText.split(",").map(p => p.trim());

  if (parts.length <= 1) {
    return secondaryText;
  }

  // we want to remove the (country from string) last part if it is in the excluded list,
  //   but if it is the only part - we want to keep it
  let finalString = parts.join(", ");
  const lastPart = parts[parts.length - 1];

  if (AUTOCOMPLETE_COUNTRY_NAMES_EXCLUDED.has(lastPart.toLocaleLowerCase())) {
    finalString = parts.slice(0, -1).join(", ");
  }

  return finalString;
};
